import { useState, useEffect, useRef } from "react";
import { motion, useInView } from "framer-motion";
import { X, Maximize2 } from "lucide-react";
import axios from "axios";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATS = ["Wszystkie", "blond", "brunetka", "bronde", "kolory kreatywne", "szkolenia"];

const FadeUp = ({ children, delay = 0, className = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay }} className={className}>
      {children}
    </motion.div>
  );
};

export default function Gallery() {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState("Wszystkie");
  const [lightbox, setLightbox] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const url = active !== "Wszystkie" ? `${API}/gallery?category=${encodeURIComponent(active)}` : `${API}/gallery`;
    setLoading(true);
    axios.get(url).then(r => { setItems(r.data); setLoading(false); }).catch(console.error);
  }, [active]);

  return (
    <div className="bg-brand-base min-h-screen">
      <Navbar />

      {/* Hero */}
      <section data-testid="gallery-hero" className="relative min-h-[50vh] flex items-end pt-24 overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1554519515-242161756769?q=80&w=1600&auto=format&fit=crop"
            alt="Galeria" className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(5,5,5,0.5) 0%, rgba(5,5,5,1) 80%)" }} />
        </div>
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 pb-14 w-full relative">
          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <p className="font-outfit text-xs uppercase tracking-[0.3em] text-violet-400 mb-4">Portfolio</p>
            <h1 className="font-anton text-6xl sm:text-8xl md:text-[10vw] text-white uppercase leading-none">
              GALERIA
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Filters */}
      <section className="max-w-screen-2xl mx-auto px-6 md:px-12 pt-12 pb-8">
        <div className="flex flex-wrap gap-3">
          {CATS.map(c => (
            <button key={c} data-testid={`gallery-filter-${c.replace(/ /g,"-")}`} onClick={() => setActive(c)}
              className={`px-6 py-2 text-xs uppercase tracking-[0.2em] transition-all border ${active === c ? "border-violet-600 text-white bg-violet-600/10" : "border-white/10 text-white/40 hover:border-white/30 hover:text-white"}`}>
              {c}
            </button>
          ))}
        </div>
      </section>

      {/* Gallery grid */}
      <section data-testid="gallery-grid" className="max-w-screen-2xl mx-auto px-6 md:px-12 pb-24">
        {loading ? (
          <div className="py-20 text-center text-white/40">Ładowanie...</div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
            {items.map((item, i) => (
              <div
                key={item.id || i}
                className="break-inside-avoid mb-2 relative overflow-hidden cursor-pointer"
                data-testid={`gallery-item-${i}`}
                onClick={() => setLightbox(item)}
              >
              <img
                src={item.thumbnail_url || item.image_url}
                alt={item.title}
                loading="lazy"
                decoding="async"
                onError={(e) => {
                  if (e.currentTarget.src !== item.image_url) {
                    e.currentTarget.src = item.image_url;
                  }
                }}
                className="w-full aspect-[3/4] object-cover"
              />

                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 hover:opacity-100 transition-opacity duration-200">
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <div className="text-xs uppercase tracking-[0.2em] text-violet-400">
                      {item.category}
                    </div>
                    <div className="text-white font-outfit font-medium text-sm mt-1">
                      {item.title}
                    </div>
                  </div>

                  <div className="absolute top-3 right-3 text-white/60">
                    <Maximize2 size={16} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && items.length === 0 && (
          <div className="py-20 text-center text-white/30">
            Brak zdjęć w tej kategorii.
          </div>
        )}
      </section>
      {/* <section data-testid="gallery-grid" className="max-w-screen-2xl mx-auto px-6 md:px-12 pb-24">
        {loading ? (
          <div className="py-20 text-center text-white/40">Ładowanie...</div>
        ) : (
          <div className="columns-2 md:columns-2 lg:columns-3 xl:columns-4 gap-2 space-y-2">
            {items.map((item, i) => (
              <FadeUp key={item.id} delay={i * 0.04}>
                <div className="break-inside-avoid mb-2 group relative overflow-hidden cursor-pointer"
                  data-testid={`gallery-item-${i}`} onClick={() => setLightbox(item)}>
                  <img src={item.image_url} alt={item.title}
                    className="w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="absolute bottom-0 left-0 right-0 p-4">
                      <div className="text-xs uppercase tracking-[0.2em] text-violet-400">{item.category}</div>
                      <div className="text-white font-outfit font-medium text-sm mt-1">{item.title}</div>
                    </div>
                    <div className="absolute top-3 right-3 text-white/60">
                      <Maximize2 size={16} />
                    </div>
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
        )}
        {!loading && items.length === 0 && (
          <div className="py-20 text-center text-white/30">Brak zdjęć w tej kategorii.</div>
        )}
      </section> */}

      {/* Lightbox */}
      {lightbox && (
        <div data-testid="gallery-lightbox" className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}>
          <button data-testid="lightbox-close" className="absolute top-6 right-6 text-white/60 hover:text-white z-10" onClick={() => setLightbox(null)}>
            <X size={28} />
          </button>
          <div onClick={e => e.stopPropagation()} className="max-w-4xl w-full max-h-[90vh]">
            <img src={lightbox.image_url} alt={lightbox.title} className="w-full max-h-[80vh] object-contain" />
            <div className="mt-4 flex justify-between items-center">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-violet-400">{lightbox.category}</div>
                <div className="text-white font-outfit font-medium mt-1">{lightbox.title}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
